<template>
  <v-dialog
    v-model="isPopupOpen"
    width="530"
    content-class="generate-cdt-modal"
    @click:outside="closeModal"
    @keydown.esc="closeModal"
  >
    <!-- for unsaved-data-mixin to work we need the modal to handle it's own state.
    this creates some problems since we need to use the activator slot provided by vuetify from this components parent.
    however, this component is used in multiple places, so we want to pass in different buttons as the activator.
    here we bind every scoped slot passed to this component to v-dialog,
    so when we pass 'template v-slot:activator="{ on }"' to this component, the slot is passed to v-dialog -->
    <template v-for="(_, slot) in $slots" #[slot]="props">
      <slot v-bind="props" :name="slot" />
    </template>
    <BaseDialogCard :title="title" @close="closeModal">
      <v-container fluid style="max-height: 400px; padding: 0">
        <v-row class="d-flex flex-column mb-0">
          <v-col class="pl-0 pb-0 pr-4">
            <p class="mb-0 sub-title">{{ subtitle }} {{ selectedCannGroup.name }} ?</p>
          </v-col>
          <v-col v-if="includeOptions" class="d-flex py-0 pr-2 justify-end">
            <MainDropdownMenu
              :actions="actions"
              @select-all="updatedSelectedCannGroups(true)"
              @unselect-all="updatedSelectedCannGroups(false)"
            />
          </v-col>
          <v-col class="pl-0 pr-0 pt-0 mt-4 pb-4 main-container">
            <v-row
              v-for="cannGroup in cannGroups"
              :key="cannGroup.key"
              class="cann-group-row d-flex justify-start"
            >
              <v-checkbox
                :id="cannGroup.key"
                :disabled="cannGroup.disabled || isCannGroupRunning(cannGroup.key)"
                class="mr-2 flex-shrink-0"
                :model-value="cannGroup.selected"
                @update:model-value="cannGroup.selected = $event"
              />
              <span class="cann-group-name pb-0">{{ cannGroup.name }}</span>
            </v-row>
          </v-col>
        </v-row>
      </v-container>
      <template #footer>
        <PageActions
          :has-data-errors="false"
          :has-data-changes="hasDataChanges"
          :save-btn-text="saveButtonText"
          @discard="discard"
          @save="onSave"
        >
          <template #right-btns>
            <v-btn class="text-outline" variant="text" @click="closeModal">
              {{ $t('actions.cancel') }}
            </v-btn>
          </template>
          <template #left-btns>
            <v-btn secondary variant="text" @click="updatedSelectedCannGroups(true)">
              {{ tkey('selectAll') }}
            </v-btn>
          </template>
        </PageActions>
      </template>
    </BaseDialogCard>
  </v-dialog>

  <!-- Confirmation dialog -->
  <v-dialog
    v-model="isRegenerateCDTModalOpen"
    retain-focus
    max-width="450"
    content-class="rtls-dialog"
    @keydown.esc="closeRegenerateCdtModal"
    @click:outside="closeRegenerateCdtModal"
  >
    <BaseDialogCard
      :title="$t('customerDecisionTree.confirmation.title')"
      :border="true"
      @close="closeRegenerateCdtModal"
    >
      <v-card-text class="text-h6">
        <p>{{ $t('customerDecisionTree.confirmation.question') }}</p>
        <strong>{{ $t('customerDecisionTree.confirmation.warning') }}</strong>
      </v-card-text>

      <template #footer>
        <v-card-actions class="justify-center">
          <v-btn primary @click="closeRegenerateCdtModal">
            {{ $t('actions.cancel') }}
          </v-btn>
          <v-btn secondary variant="flat" class="outlined-btn" @click="startCdtGeneration">
            {{ $t('actions.ok') }}
          </v-btn>
        </v-card-actions>
      </template>
    </BaseDialogCard>
  </v-dialog>
</template>

<script>
import { useInputValidations } from '@/composables/input-validations';
import { useInternationalization } from '@/composables/use-internationalization';
import { jobApi } from '@/helpers';
import { useClusteringStore } from '@/stores/clustering';
import { useScenarioCdtsStore } from '@/stores/scenario-cdts';
import { useScenariosStore } from '@/stores/scenarios';
import cannGroupUtils from '@/utils/cann-groups';
import { cloneDeep, filter, get, keyBy, keys, map, reject, isEqual } from 'lodash';
import { mapState } from 'pinia';

export default {
  props: {
    selectedCannGroup: {
      required: true,
      type: Object,
    },
    title: {
      required: true,
      type: String,
    },
    subtitle: {
      required: true,
      type: String,
    },
    includeOptions: {
      required: false,
      type: Boolean,
      default: false,
    },
    excludeSelf: {
      required: false,
      type: Boolean,
      default: false,
    },
    saveButtonText: {
      required: false,
      type: String,
      default: 'generate',
    },
    // by default, this component performs a few checks to see if a cann group can be selected.
    enableAllOptions: {
      required: false,
      type: Boolean,
      default: false,
    },
    // indicates if attribute list changes have been made on the parent CDT page
    hasAttributeListChanges: {
      required: false,
      type: Boolean,
      default: false,
    },
  },

  emits: ['confirm'],

  setup() {
    const {
      required,
      isInteger,
      isNumber,
      isLessThan,
      isAlphanumeric,
      isPositive,
      isNotEmpty,
      isGreaterThan,
      isGreaterOrEqual,
      isNameUnique,
    } = useInputValidations();
    const { tkey } = useInternationalization('customerDecisionTree.dialog');

    return {
      required,
      isInteger,
      isNumber,
      isLessThan,
      isAlphanumeric,
      isPositive,
      isNotEmpty,
      isGreaterThan,
      isGreaterOrEqual,
      isNameUnique,
      tkey,
    };
  },

  data() {
    return {
      initialCannGroups: null,
      cannGroups: null,
      isPopupOpen: false,
      isRegenerateCDTModalOpen: false,
      actions: [
        {
          name: this.tkey('selectAll'),
          value: 'selectAll',
        },
        {
          name: this.tkey('unselectAll'),
          value: 'unselectAll',
        },
      ],
    };
  },

  computed: {
    ...mapState(useClusteringStore, ['getScenarioAttributes']),
    ...mapState(useScenarioCdtsStore, ['scenarioCdts']),
    ...mapState(useScenariosStore, ['selectedScenario', 'getJob']),
    runningCannGroups() {
      const job = this.getJob('cdtGeneration');
      return filter(keys(job), k => {
        const status = get(job, `${k}.status`);
        if (!status) return false;
        return jobApi.isRunningStatus(status);
      });
    },
    hasCannGroupSelectionChanges() {
      if (!this.initialCannGroups || !this.cannGroups) return false;
      const getSelectedKeys = groups => map(filter(groups, 'selected'), 'key').sort();
      return !isEqual(getSelectedKeys(this.initialCannGroups), getSelectedKeys(this.cannGroups));
    },
    hasDataChanges() {
      return this.hasCannGroupSelectionChanges || this.hasAttributeListChanges;
    },
    selectedCannGroupsWithExistingCdt() {
      if (!this.cannGroups) return [];
      const scenarioCdtsMap = keyBy(this.scenarioCdts, 'cannGroupId');
      return this.cannGroups.filter(cg => {
        if (!cg.selected) return false;
        const cdtDoc = scenarioCdtsMap[cg.key];
        // A CDT has been generated if it has a cdt array with content
        return cdtDoc && cdtDoc.cdt && cdtDoc.cdt.length > 0;
      });
    },
    anySelectedCannGroupHasExistingCdt() {
      return this.selectedCannGroupsWithExistingCdt.length > 0;
    },
  },

  watch: {
    selectedCannGroup: {
      immediate: true,
      handler() {
        this.cannGroups = this.getCannGroups().map(gc => {
          gc.selected = false;
          return gc;
        });
        const { key } = this.selectedCannGroup;
        const scenarioCdtsMap = keyBy(this.scenarioCdts, 'cannGroupId');
        if (!this.cannGroups) {
          return;
        }
        if (this.excludeSelf) {
          // for the copy selection, we do not show the active CDT.
          this.cannGroups = reject(this.cannGroups, { key });
        }
        if (this.enableAllOptions) {
          this.initialCannGroups = cloneDeep(this.cannGroups);
          return;
        }
        // for each CG, check if they can be selected
        this.cannGroups = this.cannGroups.map(cg => {
          const savedDocument = scenarioCdtsMap[cg.key];
          // if the cann group has no attributes selected, it cannot have the CDT generated
          if (savedDocument && savedDocument.attributes) {
            cg.attributeSelection = savedDocument.attributes;
            cg.disabled = savedDocument.attributes.length < 1 || cg.key === key;
          } else if (!savedDocument) {
            // if the cann group was not stored into the DB, the user must first save it then generate CDTs.
            cg.disabled = cg.key === key;
          }
          cg.selected = cg.key === key;
          return cg;
        });
        this.initialCannGroups = cloneDeep(this.cannGroups);
      },
    },
  },

  created() {
    this.initialCannGroups = cloneDeep(this.cannGroups);
  },

  methods: {
    closeModal() {
      this.isPopupOpen = false;
    },

    discard() {
      this.cannGroups = cloneDeep(this.initialCannGroups);
    },

    onSave() {
      // Check if any selected cann groups already have generated CDTs
      if (this.anySelectedCannGroupHasExistingCdt) {
        this.isRegenerateCDTModalOpen = true;
        return;
      }
      this.triggerCdtGeneration();
    },

    closeRegenerateCdtModal() {
      this.isRegenerateCDTModalOpen = false;
    },

    startCdtGeneration() {
      this.closeRegenerateCdtModal();
      this.triggerCdtGeneration();
    },

    triggerCdtGeneration() {
      const selectedCannGroupIds = map(
        this.cannGroups.filter(cg => cg.selected),
        'key'
      );
      this.$emit('confirm', { cannGroupIds: selectedCannGroupIds });
      this.discard();
      this.closeModal();
    },

    getCannGroups() {
      return cloneDeep(cannGroupUtils.getLeafNodes(this.selectedScenario.cannGroups));
    },

    isCannGroupRunning(key) {
      return this.runningCannGroups.includes(key);
    },

    isSelectable(cannGroup) {
      return !(cannGroup.disabled || this.isCannGroupRunning(cannGroup.key));
    },

    updatedSelectedCannGroups(shouldBeSelected) {
      const isCurrentCannGroup = ({ key }) => key === this.selectedCannGroup.key;

      this.cannGroups = this.cannGroups.map(cg => {
        const shouldBeEnabled = this.isSelectable(cg) && shouldBeSelected;

        const selected = isCurrentCannGroup(cg) || shouldBeEnabled;

        return {
          ...cg,
          selected,
        };
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.sub-title {
  font-size: 1.2rem;
  font-weight: bold;
}

.main-container {
  border-bottom: 1px solid $assortment-divider-colour;
  max-height: 800px;
  overflow-y: auto;
  flex-basis: auto;
}

.cann-group-row {
  border-style: solid;
  border-width: 1px;
  color: $assortment-cann-group-row-colour;
  opacity: 1;
  padding: 3px;
  align-items: center;
  flex-wrap: nowrap;

  &:nth-child(odd) {
    background-color: $assortment-cann-group-row-bg-blue;
    border-color: $assortment-cann-group-row-bg-blue;
  }
  &:nth-child(even) {
    background-color: $assortment-cann-group-row-bg-white;
    border-color: $assortment-cann-group-row-bg-white;
  }

  .cann-group-name {
    word-break: break-word;
    font-size: 1.1rem;
  }
}

.rtls-checkbox {
  :deep(.v-input--selection-controls__input) {
    margin-right: 0px !important;
  }
}

:deep(.dialog-card__footer) {
  border-top-width: 0px;
}

:deep(.base-dialog-card__content) {
  padding-bottom: 0 !important;
}
</style>
